import { useState, useEffect } from 'react';
import { merchantAPI } from '../../services/api';
import { UserPlus, Trash2, X, Eye, EyeOff, Key, Ban, CheckCircle, Shield } from 'lucide-react';
import toast from 'react-hot-toast';

const SECTIONS = [
  { key: 'inbox', label: 'Inbox' },
  { key: 'bookings', label: 'Bookings' },
  { key: 'customers', label: 'Customers' },
  { key: 'channels', label: 'Channels' },
  { key: 'integrations', label: 'Integrations' },
  { key: 'ai_settings', label: 'AI Settings' },
  { key: 'extraction', label: 'Extraction' },
  { key: 'billing', label: 'Billing' },
  { key: 'agents', label: 'Agents' },
];

export function AgentsPage() {
  const [agents, setAgents] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', password: '', permissions: [] });
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);

  // Reset password modal
  const [resetAgent, setResetAgent] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [showNewPass, setShowNewPass] = useState(false);

  // Permissions modal
  const [permAgent, setPermAgent] = useState(null);
  const [permSelections, setPermSelections] = useState([]);

  useEffect(() => {
    merchantAPI.getAgents().then(({ data }) => setAgents(data.data)).catch(() => {});
  }, []);

  const handleAdd = async () => {
    if (!form.name || !form.email || !form.password) return toast.error('Fill all fields');
    if (form.password.length < 8) return toast.error('Password must be at least 8 characters');
    setLoading(true);
    try {
      const { data } = await merchantAPI.inviteAgent(form);
      setAgents(prev => [...prev, data.data]);
      setShowAdd(false);
      setForm({ name: '', email: '', password: '', permissions: [] });
      toast.success('Agent added');
    } catch (_) {}
    finally { setLoading(false); }
  };

  const handleRemove = async (id) => {
    if (!window.confirm('Remove this agent? This cannot be undone.')) return;
    try {
      await merchantAPI.removeAgent(id);
      setAgents(prev => prev.filter(a => a._id !== id));
      toast.success('Agent removed');
    } catch (_) {}
  };

  const handleBlock = async (agent) => {
    const newActive = !agent.isActive;
    try {
      await merchantAPI.updateAgent(agent._id, { isActive: newActive });
      setAgents(prev => prev.map(a => a._id === agent._id ? { ...a, isActive: newActive } : a));
      toast.success(newActive ? 'Agent unblocked' : 'Agent blocked');
    } catch (_) {}
  };

  const handleResetPassword = async () => {
    if (!newPassword || newPassword.length < 8) return toast.error('Password must be at least 8 characters');
    try {
      await merchantAPI.resetAgentPassword(resetAgent._id, { password: newPassword });
      toast.success('Password updated');
      setResetAgent(null);
      setNewPassword('');
    } catch (_) {}
  };

  const handleSavePermissions = async () => {
    try {
      const { data } = await merchantAPI.updateAgent(permAgent._id, { permissions: permSelections });
      setAgents(prev => prev.map(a => a._id === permAgent._id ? data.data : a));
      toast.success('Permissions updated');
      setPermAgent(null);
    } catch (_) {}
  };

  const toggleFormPerm = (key) => {
    setForm(p => ({
      ...p,
      permissions: p.permissions.includes(key)
        ? p.permissions.filter(k => k !== key)
        : [...p.permissions, key],
    }));
  };

  const toggleEditPerm = (key) => {
    setPermSelections(p => p.includes(key) ? p.filter(k => k !== key) : [...p, key]);
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Agents</h1>
          <p className="page-subtitle">Team members who can access your workspace</p>
        </div>
        <button className="btn btn-primary" onClick={() => setShowAdd(true)}><UserPlus size={15} /> Add Agent</button>
      </div>
      <div className="page-body">
        <div className="card" style={{ padding: 0 }}>
          {agents.length === 0 ? (
            <div className="empty-state" style={{ padding: 60 }}>
              <UserPlus size={36} /><h3>No agents yet</h3><p>Add team members to help manage conversations</p>
            </div>
          ) : (
            <table>
              <thead>
                <tr><th>Name</th><th>Email</th><th>Status</th><th>Permissions</th><th>Last Login</th><th>Actions</th></tr>
              </thead>
              <tbody>
                {agents.map(agent => (
                  <tr key={agent._id} style={{ opacity: agent.isActive === false ? 0.5 : 1 }}>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <div className="avatar avatar-sm">{agent.name?.[0]}</div>
                        <span style={{ fontWeight: 500 }}>{agent.name}</span>
                      </div>
                    </td>
                    <td><span style={{ color: 'var(--text-muted)', fontSize: '0.82rem' }}>{agent.email}</span></td>
                    <td>
                      {agent.isActive === false
                        ? <span className="badge badge-red">Blocked</span>
                        : <span className="badge badge-green">Active</span>
                      }
                    </td>
                    <td>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                        {(!agent.permissions || agent.permissions.length === 0)
                          ? <span className="badge badge-gray" style={{ fontSize: '0.68rem' }}>All access</span>
                          : agent.permissions.map(p => (
                            <span key={p} className="badge badge-blue" style={{ fontSize: '0.68rem' }}>{p.replace('_', ' ')}</span>
                          ))
                        }
                      </div>
                    </td>
                    <td><span style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>{agent.lastLogin ? new Date(agent.lastLogin).toLocaleDateString() : 'Never'}</span></td>
                    <td>
                      <div style={{ display: 'flex', gap: 4 }}>
                        <button className="btn btn-ghost btn-sm" title="Permissions" onClick={() => { setPermAgent(agent); setPermSelections(agent.permissions || []); }}>
                          <Shield size={13} />
                        </button>
                        <button className="btn btn-ghost btn-sm" title="Reset Password" onClick={() => { setResetAgent(agent); setNewPassword(''); setShowNewPass(false); }}>
                          <Key size={13} />
                        </button>
                        <button className={`btn btn-ghost btn-sm`} title={agent.isActive === false ? 'Unblock' : 'Block'} onClick={() => handleBlock(agent)}>
                          {agent.isActive === false ? <CheckCircle size={13} style={{ color: 'var(--green)' }} /> : <Ban size={13} />}
                        </button>
                        <button className="btn btn-danger btn-sm" title="Remove" onClick={() => handleRemove(agent._id)}>
                          <Trash2 size={13} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Add Agent Modal */}
      {showAdd && (
        <div className="modal-overlay" onClick={() => setShowAdd(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <span className="modal-title">Add Agent</span>
              <button className="modal-close" onClick={() => setShowAdd(false)}><X size={18} /></button>
            </div>
            <div className="form-group">
              <label className="form-label">Name</label>
              <input className="form-input" value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} />
            </div>
            <div className="form-group">
              <label className="form-label">Email</label>
              <input className="form-input" type="email" value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} />
            </div>
            <div className="form-group">
              <label className="form-label">Password</label>
              <div style={{ position: 'relative' }}>
                <input className="form-input" type={showPass ? 'text' : 'password'} value={form.password}
                  onChange={e => setForm(p => ({ ...p, password: e.target.value }))}
                  style={{ paddingRight: 40 }} />
                <button type="button" onClick={() => setShowPass(p => !p)}
                  style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer' }}>
                  {showPass ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Sidebar Access</label>
              <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: 8 }}>Select which sections this agent can see. Leave empty for full access.</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {SECTIONS.map(s => {
                  const active = form.permissions.includes(s.key);
                  return (
                    <button key={s.key} type="button" onClick={() => toggleFormPerm(s.key)}
                      style={{
                        padding: '5px 12px', borderRadius: 6, fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer',
                        border: `1.5px solid ${active ? 'var(--primary)' : 'var(--border)'}`,
                        background: active ? 'var(--primary-light)' : 'transparent',
                        color: active ? 'var(--primary)' : 'var(--text-muted)',
                      }}>
                      {s.label}
                    </button>
                  );
                })}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button className="btn btn-secondary" onClick={() => setShowAdd(false)}>Cancel</button>
              <button className="btn btn-primary" disabled={loading} onClick={handleAdd}><UserPlus size={14} /> Add Agent</button>
            </div>
          </div>
        </div>
      )}

      {/* Reset Password Modal */}
      {resetAgent && (
        <div className="modal-overlay" onClick={() => setResetAgent(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <span className="modal-title">Reset Password</span>
              <button className="modal-close" onClick={() => setResetAgent(null)}><X size={18} /></button>
            </div>
            <p style={{ fontSize: '0.82rem', color: 'var(--text-muted)', marginBottom: 14 }}>
              Set a new password for <strong>{resetAgent.name}</strong> ({resetAgent.email})
            </p>
            <div className="form-group">
              <label className="form-label">New Password</label>
              <div style={{ position: 'relative' }}>
                <input className="form-input" type={showNewPass ? 'text' : 'password'} value={newPassword}
                  onChange={e => setNewPassword(e.target.value)} placeholder="Minimum 8 characters"
                  style={{ paddingRight: 40 }} />
                <button type="button" onClick={() => setShowNewPass(p => !p)}
                  style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer' }}>
                  {showNewPass ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button className="btn btn-secondary" onClick={() => setResetAgent(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleResetPassword}><Key size={13} /> Update Password</button>
            </div>
          </div>
        </div>
      )}

      {/* Permissions Modal */}
      {permAgent && (
        <div className="modal-overlay" onClick={() => setPermAgent(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <span className="modal-title">Permissions</span>
              <button className="modal-close" onClick={() => setPermAgent(null)}><X size={18} /></button>
            </div>
            <p style={{ fontSize: '0.82rem', color: 'var(--text-muted)', marginBottom: 14 }}>
              Choose which sidebar sections <strong>{permAgent.name}</strong> can access. Leave empty for full access.
            </p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 20 }}>
              {SECTIONS.map(s => {
                const active = permSelections.includes(s.key);
                return (
                  <button key={s.key} type="button" onClick={() => toggleEditPerm(s.key)}
                    style={{
                      padding: '7px 16px', borderRadius: 8, fontSize: '0.82rem', fontWeight: 600, cursor: 'pointer',
                      border: `2px solid ${active ? 'var(--primary)' : 'var(--border)'}`,
                      background: active ? 'var(--primary-light)' : 'transparent',
                      color: active ? 'var(--primary)' : 'var(--text-muted)',
                      transition: 'all 0.15s',
                    }}>
                    {active && <CheckCircle size={12} style={{ marginRight: 5, verticalAlign: -1 }} />}
                    {s.label}
                  </button>
                );
              })}
            </div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button className="btn btn-secondary" onClick={() => setPermAgent(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleSavePermissions}><Shield size={13} /> Save Permissions</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AgentsPage;
